import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import usePromptService from '../../services/promptService';

const PromptHistoryModal = ({ prompt, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedVersion, setSelectedVersion] = useState(null);

  const promptService = usePromptService();
  const itemsPerPage = 10;

  useEffect(() => {
    loadHistory();
  }, [currentPage, prompt.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await promptService.getPromptHistory(prompt.id, currentPage, itemsPerPage);
      setHistory(response.data.histories || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      toast.error('Failed to load prompt history');
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-green-100 text-green-800';
      case 'UPDATE': return 'bg-blue-100 text-blue-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      case 'RESTORE': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  const handleVersionClick = (historyItem) => {
    if (selectedVersion?.id === historyItem.id) {
      setSelectedVersion(null);
    } else {
      setSelectedVersion(historyItem);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl  w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center border-b border-gray-200 p-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Prompt History</h3>
            <p className="text-sm text-gray-500">{prompt.name} - Version {prompt.version}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          <div className="w-1/3 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h4 className="font-medium text-gray-900">Version History</h4>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : history.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No history available
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {history.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleVersionClick(item)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 ${
                        selectedVersion?.id === item.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          Version {item.version}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionColor(item.action)}`}>
                          {item.action}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>{new Date(item.createdAt).toLocaleString()}</div>
                        {item.changedBy && <div>By: {item.changedBy}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className="p-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-500">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h4 className="font-medium text-gray-900">
                {selectedVersion 
                  ? `Version ${selectedVersion.version} Content` 
                  : `Current Version ${prompt.version} Content`
                }
              </h4>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-2">
                    {selectedVersion ? `Version ${selectedVersion.version}` : 'Current Version'} Content
                  </h5>
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono bg-white p-3 rounded border">
                    {selectedVersion ? selectedVersion.content : prompt.content}
                  </pre>
                </div>

                {selectedVersion && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-2">Version Details</h5>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <dt className="text-blue-700 font-medium">Version:</dt>
                      <dd className="text-blue-900">{selectedVersion.version}</dd>
                      <dt className="text-blue-700 font-medium">Action:</dt>
                      <dd className="text-blue-900">{selectedVersion.action}</dd>
                      <dt className="text-blue-700 font-medium">Date:</dt>
                      <dd className="text-blue-900">{new Date(selectedVersion.createdAt).toLocaleString()}</dd>
                      {selectedVersion.changedBy && (
                        <>
                          <dt className="text-blue-700 font-medium">Changed by:</dt>
                          <dd className="text-blue-900">{selectedVersion.changedBy}</dd>
                        </>
                      )}
                      {selectedVersion.reasonForChange && (
                        <>
                          <dt className="text-blue-700 font-medium">Reason for change:</dt>
                          <dd className="text-blue-900">{selectedVersion.reasonForChange}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptHistoryModal;